/**
 * SIMON AI OS — 核心服务层
 * 
 * 模拟多 Agent 协同决策引擎后端
 * 生产环境替换为真实 LangGraph + Java 微服务接口
 * 
 * Agent 调度链路:
 * 用户输入 → Intent Agent → DecisionContext 构建 → 多 Agent 并行 → 
 * Simulation 仿真校验 → Explainability 解释生成 → 方案输出
 */

// ═══════════════════════════════════════════════════
// Agent 注册中心 — 全矩阵 Agent 定义
// ═══════════════════════════════════════════════════

export const AGENT_REGISTRY = {
  // P0 核心底层
  intent: { id: 'intent', name: '意图解析 Agent', level: 'P0', icon: '🎯', color: '#6366f1', desc: '解析自然语言场景需求，提取约束因子' },
  decision: { id: 'decision', name: '决策引擎 Agent', level: 'P0', icon: '⚡', color: '#8b5cf6', desc: '构建 DecisionContext，统一决策上下文' },
  memory: { id: 'memory', name: '记忆 Agent', level: 'P0', icon: '🧠', color: '#a78bfa', desc: '管理分层记忆体系' },
  task: { id: 'task', name: 'Task Execution Agent', level: 'P0', icon: '🔧', color: '#c084fc', desc: '全场景事务落地执行' },

  // P1 业务增强
  life: { id: 'life', name: 'Life Scenario Agent', level: 'P1', icon: '🌟', color: '#e8d28a', desc: '场景识别超级中枢' },
  weather: { id: 'weather', name: '天气 Agent', level: 'P1', icon: '🌤️', color: '#a8c7d8', desc: '实时天气与预测数据' },
  poi: { id: 'poi', name: 'POI 检索 Agent', level: 'P1', icon: '📍', color: '#7dd3fc', desc: '地点检索与筛选' },
  dining: { id: 'dining', name: '餐饮 Agent', level: 'P1', icon: '🍽️', color: '#fbbf24', desc: '餐厅筛选与评价分析' },
  route: { id: 'route', name: '路线 Agent', level: 'P1', icon: '🗺️', color: '#34d399', desc: '路线规划与交通方案' },
  budget: { id: 'budget', name: '预算 Agent', level: 'P1', icon: '💰', color: '#f87171', desc: '预算约束与优化' },
  booking: { id: 'booking', name: '预约 Agent', level: 'P1', icon: '📅', color: '#fb923c', desc: '预约创建与档期管理' },
  simulation: { id: 'simulation', name: '行程模拟', level: 'P1', icon: '⏳', color: '#f59e0b', desc: '模拟不同人群的行程体验' },
  risk: { id: 'risk', name: '风险分析', level: 'P1', icon: '⚠️', color: '#ef4444', desc: '分析天气、交通、商家风险' },
  explain: { id: 'explain', name: '决策解释', level: 'P1', icon: '🔍', color: '#8b5cf6', desc: '解释推荐理由和拒绝原因' },
  policy: { id: 'policy', name: '策略引擎', level: 'P1', icon: '🔒', color: '#06b6d4', desc: '管理 Agent 操作权限' },
  replan: { id: 'replan', name: '动态重规划', level: 'P1', icon: '🔄', color: '#10b981', desc: '异常事件触发重新规划' },

  // P2 商业化
  commerce: { id: 'commerce', name: '购物决策', level: 'P2', icon: '🛒', color: '#ec4899', desc: '商品搜索、比价、评价分析' },
  local_service: { id: 'local_service', name: '本地服务', level: 'P2', icon: '🔧', color: '#14b8a6', desc: '线下服务匹配与预约' },
  business: { id: 'business', name: '商家分析', level: 'P2', icon: '📊', color: '#f97316', desc: '商家经营数据分析' },
};

// ═══════════════════════════════════════════════════
// 场景模板 — 预设快速场景
// ═══════════════════════════════════════════════════

export const SCENARIO_TEMPLATES = [
  { 
    id: 'date-night', 
    label: '周末约会', 
    icon: '💕', 
    input: '周六晚上和女朋友约会，预算 500 元，想在市中心找一家氛围好的日料餐厅，饭后还想散步喝咖啡',
    agents: ['intent', 'life', 'poi', 'dining', 'budget', 'route', 'booking'],
  },
  { 
    id: 'business-trip', 
    label: '商务出行', 
    icon: '💼', 
    input: '下周三去北京出差两天，需要安排机票、酒店、商务晚宴，预算 3000 元',
    agents: ['intent', 'life', 'route', 'dining', 'budget', 'booking', 'weather'],
  },
  { 
    id: 'family-day', 
    label: '亲子周末', 
    icon: '👨‍👩‍👧', 
    input: '周日带 5 岁孩子去公园玩耍，预算 200 元，需要午餐和下午茶安排',
    agents: ['intent', 'life', 'poi', 'weather', 'budget', 'route', 'risk'],
  },
  { 
    id: 'shopping', 
    label: '购物决策', 
    icon: '🛍️', 
    input: '想给男朋友买一款 5000 元左右的机械键盘，做程序员用，想要红轴静音款',
    agents: ['intent', 'commerce', 'budget', 'risk', 'explain'],
  },
];

// ═══════════════════════════════════════════════════
// 模拟 Agent 调度引擎
// ═══════════════════════════════════════════════════

const delay = (ms) => new Promise(r => setTimeout(r, ms));

/**
 * 执行 Agent 调度链路
 * @param {string} userInput - 用户自然语言输入
 * @param {function} onAgentStart - Agent 启动回调
 * @param {function} onAgentComplete - Agent 完成回调
 * @returns {Promise<object>} 完整决策方案
 */
export async function executeAgentPipeline(userInput, onAgentStart, onAgentComplete) {
  // Step 1: 意图解析
  onAgentStart?.('intent');
  await delay(600);
  const intentResult = parseIntent(userInput);
  onAgentComplete?.('intent', intentResult);

  // Step 2: 构建 DecisionContext
  onAgentStart?.('decision');
  await delay(500);
  const decisionContext = buildDecisionContext(intentResult);
  onAgentComplete?.('decision', decisionContext);

  // Step 3: 并行调度业务 Agent
  const activeAgents = selectAgents(intentResult);
  const agentResults = {};

  for (const agentId of activeAgents) {
    onAgentStart?.(agentId);
    await delay(400 + Math.random() * 400);
    const result = simulateAgentResult(agentId, intentResult, decisionContext);
    agentResults[agentId] = result;
    onAgentComplete?.(agentId, result);
  }

  // Step 4: 仿真校验
  onAgentStart?.('simulation');
  await delay(800);
  const simulation = runSimulation(intentResult, agentResults);
  onAgentComplete?.('simulation', simulation);

  // Step 5: 生成解释
  onAgentStart?.('explain');
  await delay(500);
  const explanation = generateExplanation(intentResult, agentResults, simulation);
  onAgentComplete?.('explain', explanation);

  // Step 6: 最终方案
  return assemblePlan(userInput, intentResult, decisionContext, agentResults, simulation, explanation);
}

// ═══════════════════════════════════════════════════
// 内部模拟函数
// ═══════════════════════════════════════════════════

function parseIntent(input) {
  const intents = [];
  
  // 简单关键词匹配模拟 NLP
  if (/约会|女朋友|情侣|浪漫/.test(input)) intents.push({ type: 'scenario', value: 'romantic_date', confidence: 0.95 });
  if (/出差|商务|工作/.test(input)) intents.push({ type: 'scenario', value: 'business_trip', confidence: 0.92 });
  if (/亲子|孩子|带娃|家庭/.test(input)) intents.push({ type: 'scenario', value: 'family_day', confidence: 0.88 });
  if (/买|购物|选购/.test(input)) intents.push({ type: 'scenario', value: 'shopping', confidence: 0.90 });
  
  // 提取约束
  const constraints = {};
  const budgetMatch = input.match(/预算\s*(\d+)/);
  if (budgetMatch) constraints.budget = parseInt(budgetMatch[1]);
  
  const timeMatch = input.match(/周[一二三四五六日]|明天|后天|今天/);
  if (timeMatch) constraints.time = timeMatch[0];
  
  const locationMatch = input.match(/([\u4e00-\u9fa5]{2,4}(?:市|区|县|镇|路))/);
  if (locationMatch) constraints.location = locationMatch[1];
  
  // 默认兜底
  if (intents.length === 0) intents.push({ type: 'scenario', value: 'general_life', confidence: 0.75 });
  
  return { intents, constraints, originalInput: input, timestamp: Date.now() };
}

function buildDecisionContext(intent) {
  return {
    ctxId: `ctx_${Date.now()}`,
    scenario: intent.intents[0]?.value || 'general',
    constraints: intent.constraints,
    userProfile: {
     Preferences: ['安静环境', '品质优先', '不排队'],
      budgetRange: intent.constraints.budget ? [0, intent.constraints.budget] : [0, 1000],
    },
    timestamp: Date.now(),
  };
}

function selectAgents(intent) {
  const scenario = intent.intents[0]?.value;
  switch (scenario) {
    case 'romantic_date': return ['life', 'poi', 'dining', 'budget', 'route', 'weather'];
    case 'business_trip': return ['life', 'route', 'dining', 'budget', 'booking', 'weather'];
    case 'family_day': return ['life', 'poi', 'weather', 'budget', 'route', 'risk'];
    case 'shopping': return ['commerce', 'budget', 'risk'];
    default: return ['life', 'poi', 'budget'];
  }
}

function simulateAgentResult(agentId, intent, ctx) {
  const results = {
    life: {
      scenarioType: intent.intents[0]?.value,
      sceneFactors: ['氛围', '时间窗口', '交通可达性', '天气适配'],
      recommendedTimeSlots: ['18:00-22:00', '12:00-14:00'],
    },
    poi: {
      candidates: [
        { name: '月見·庭院日料', distance: '1.2km', rating: 4.8, tags: ['日料', '庭院', '约会'] },
        { name: '樱华·江景餐厅', distance: '2.5km', rating: 4.7, tags: ['日料', '江景', '氛围'] },
        { name: '水木·割烹料理', distance: '0.8km', rating: 4.9, tags: ['日料', '高端', '私密'] },
      ],
      totalFound: 28,
    },
    dining: {
      selected: { name: '月見·庭院日料', reason: '评分最高 + 庭院氛围适配约会场景 + 符合预算', avgPrice: 185 },
      alternatives: ['樱华·江景餐厅', '水木·割烹料理'],
      reviewSummary: '用户评价关键词: 环境精致(92%正面)、食材新鲜(89%正面)、服务周到(95%正面)',
    },
    budget: {
      total: intent.constraints.budget || 500,
      breakdown: { dining: 370, coffee: 80, transport: 50 },
      withinBudget: true,
      savingsTip: '餐厅好评返现可节约 20 元',
    },
    route: {
      segments: [
        { from: '您的位置', to: '月見·庭院日料', method: '步行', duration: '15min', distance: '1.2km' },
        { from: '月見·庭院日料', to: '湖畔咖啡', method: '步行', duration: '8min', distance: '0.6km' },
      ],
      totalDuration: '23min',
     	totalDistance: '1.8km',
    },
    weather: {
      forecast: '周六 多云转晴 18-24°C',
      outdoorSuitability: '良好',
      suggestion: '适合饭后江边散步',
    },
    booking: {
      available: true,
      recommendedSlot: '周六 18:30',
      reservationLink: '#',
      deposit: '无需定金',
    },
    risk: {
      overall: '低风险',
      items: [
        { type: '安全', level: 'low', desc: '该区域治安良好' },
        { type: '天气', level: 'low', desc: '无降雨预警' },
        { type: '拥挤', level: 'medium', desc: '周末晚高峰建议提前到' },
      ],
    },
    commerce: {
      candidates: [
        { name: 'Keychron K8 Pro 红轴', price: 568, match: 0.94, source: '京东' },
        { name: 'HHKB Professional', price: 1980, match: 0.87, source: '天猫' },
        { name: 'Leopold FC660M', price: 899, match: 0.91, source: '淘宝' },
      ],
      recommendation: 'Keychron K8 Pro 红轴',
      reason: '性价比最高 + 程序员静音需求匹配度 94%',
    },
  };
  return results[agentId] || { status: 'completed' };
}

function runSimulation(intent, agentResults) {
  return {
    passedChecks: ['预算约束', '时间可行性', '地理可达性', '天气适配性'],
    warnings: [
      { check: '步行距离', value: '1.8km', status: 'pass', note: '在舒适范围内' },
      { check: '疲劳度', value: '低', status: 'pass', note: '无需调整' },
      { check: '时间冲突', value: '无', status: 'pass', note: '时间窗口充裕' },
    ],
    overallScore: 92,
    recommendation: '方案可行性高，建议执行',
  };
}

function generateExplanation(intent, agentResults, simulation) {
  return {
    selectedOptions: [
      { name: '月見·庭院日料', score: 94, reasons: ['预算匹配: 370/500', '距离适中: 1.2km', '场景适配: 约会氛围', '口碑优秀: 4.8分'] },
    ],
    eliminatedOptions: [
      { name: '某网红餐厅', reason: '超出预算 30%' },
      { name: '某快餐店', reason: '不符合约会场景氛围需求' },
    ],
    confidence: 0.92,
    dataSource: '数据更新: 18 分钟前',
    overallScore: simulation.overallScore,
  };
}

function assemblePlan(input, intent, ctx, agentResults, simulation, explanation) {
  const dining = agentResults.dining?.selected || { name: '月見·庭院日料', avgPrice: 185 };
  const route = agentResults.route || {};
  const budget = agentResults.budget || { total: 500, breakdown: {} };
  
  return {
    planId: `plan_${Date.now()}`,
    title: `${intent.intents[0]?.value === 'romantic_date' ? '浪漫约会方案' : '定制生活方案'}`,
    summary: `为您规划了一套完整的${intent.intents[0]?.value === 'romantic_date' ? '约会' : '生活'}方案：${dining.name}（¥${dining.avgPrice}/人）+ 饭后咖啡散步，总预算 ¥${budget.total}，预计耗时 3 小时`,
    timeline: [
      { time: '18:00', action: '出发', detail: `从当前位置步行至${dining.name}（${route.segments?.[0]?.duration || '15min'}）`, agent: 'route' },
      { time: '18:30', action: '到达餐厅', detail: `${dining.name} - 庭院景观位已预留`, agent: 'dining' },
      { time: '18:30-20:30', action: '享用晚餐', detail: `推荐套餐：主厨精选 ¥${dining.avgPrice}/人`, agent: 'dining' },
      { time: '20:30-21:00', action: '饭后散步', detail: '沿湖畔步行至咖啡街，欣赏夜景', agent: 'route' },
      { time: '21:00-21:30', action: '咖啡时光', detail: '湖畔咖啡 - 招牌手冲', agent: 'dining' },
    ],
    budget: budget,
    weather: agentResults.weather,
    risks: agentResults.risk,
    explanation: explanation,
    simulation: simulation,
    actionable: {
      canBook: agentResults.booking?.available || false,
      bookTime: agentResults.booking?.recommendedSlot || '周六 18:30',
    },
  };
}

// ═══════════════════════════════════════════════════
// 业务逻辑函数 — 新 Agent 能力
// ═══════════════════════════════════════════════════

/**
 * 行程模拟 — 模拟不同人群的行程体验
 * @param {object} plan - 行程方案 (timeline, budget, route 等)
 * @param {string} persona - 人群类型: 'couple' | 'family' | 'solo' | 'business'
 * @returns {Promise<object>} { score, fatigue, issues, suggestions }
 */
export const simulateTrip = async (plan, persona = 'couple') => {
  await delay(500 + Math.random() * 400);
  const personaLabels = { couple: '情侣', family: '亲子家庭', solo: '独行旅客', business: '商务人士' };
  const issues = [];
  const suggestions = [];
  let fatigue = '中等';
  let score = 88;

  // 根据人群模拟差异体验
  if (persona === 'family') {
    issues.push({ type: '步数', desc: '步行总距离 1.8km，对 5 岁儿童偏长', severity: 'medium' });
    suggestions.push('建议在路线中段增加儿童休息点');
    score -= 5;
    fatigue = '偏高';
  } else if (persona === 'business') {
    issues.push({ type: '时间', desc: '晚餐时段与会议结束时间冲突', severity: 'high' });
    suggestions.push('建议将晚餐调整为 19:30 开始');
    score -= 8;
    fatigue = '高';
  } else if (persona === 'solo') {
    score += 2;
    fatigue = '低';
  }

  // 通用模拟
  if (plan.timeline && plan.timeline.length > 4) {
    issues.push({ type: '节奏', desc: '行程节点偏密，体验可能碎片化', severity: 'low' });
    suggestions.push('可适当减少 1-2 个节点，提升沉浸感');
  }

  if (plan.budget && plan.budget.total > 800) {
    suggestions.push('预算充裕，可考虑升级餐厅套餐');
  }

  if (issues.length === 0) {
    issues.push({ type: '综合', desc: '行程节奏合理，无明显问题', severity: 'none' });
  }

  suggestions.push(`针对${personaLabels[persona] || persona}群体：整体方案适配度良好`);

  return { score: Math.max(0, Math.min(100, score)), fatigue, issues, suggestions };
};

/**
 * 风险分析 — 分析天气、交通、商家风险
 * @param {object} plan - 行程方案
 * @returns {Promise<object>} { risks: [{ type, level, suggestion }], overallRisk }
 */
export const analyzeRisk = async (plan) => {
  await delay(400 + Math.random() * 300);
  const risks = [];

  // 天气风险
  risks.push({
    type: '天气',
    level: 'low',
    detail: '多云转晴 18-24°C，无降雨',
    suggestion: '适合户外活动，建议携带薄外套',
  });

  // 交通风险
  risks.push({
    type: '交通',
    level: 'medium',
    detail: '晚高峰时段市中心拥堵概率 65%',
    suggestion: '建议提前 15 分钟出发或选择地铁出行',
  });

  // 商家风险
  risks.push({
    type: '商家',
    level: 'low',
    detail: '目标餐厅近 30 天无差评，营业正常',
    suggestion: '建议提前 1 天确认预约状态',
  });

  // 人群拥挤风险
  if (plan.timeline) {
    const peakHours = plan.timeline.some((t) => {
      const time = t.time || '';
      return /18:|19:|20:/.test(time);
    });
    if (peakHours) {
      risks.push({
        type: '拥挤',
        level: 'medium',
        detail: '就餐时段为周末高峰，等位概率 40%',
        suggestion: '已预留景观位，建议 18:15 前到达',
      });
    }
  }

  const levelScore = { low: 1, medium: 2, high: 3 };
  const avgScore = risks.reduce((s, r) => s + levelScore[r.level], 0) / risks.length;
  const overallRisk = avgScore < 1.5 ? '低风险' : avgScore < 2.5 ? '中风险' : '高风险';

  return { risks, overallRisk };
};

/**
 * 解释推荐 — 解释为什么推荐某个方案
 * @param {object} item - 推荐条目 (餐厅/商品/路线等)
 * @param {object} context - 决策上下文 (intent, userProfile 等)
 * @returns {Promise<object>} { reasons: [{ type, text, confidence }], overallConfidence }
 */
export const explainRecommendation = async (item, context) => {
  await delay(350 + Math.random() * 250);
  const reasons = [];

  // 基于评分的原因
  if (item.rating) {
    reasons.push({
      type: '口碑',
      text: `评分 ${item.rating} 分，高于同城同类商家 85%`,
      confidence: 0.92,
    });
  }

  // 基于价格的原因
  if (item.price && context?.userProfile?.budgetRange) {
    const [min, max] = context.userProfile.budgetRange;
    if (item.price >= min && item.price <= max) {
      reasons.push({
        type: '预算',
        text: `价格 ¥${item.price} 处于您的预算区间 ¥${min}-¥${max} 内`,
        confidence: 0.95,
      });
    }
  }

  // 基于标签匹配的原因
  if (item.tags && context?.preferences) {
    const matched = item.tags.filter((t) => context.preferences.includes(t));
    if (matched.length > 0) {
      reasons.push({
        type: '偏好匹配',
        text: `标签「${matched.join('、')}」与您的偏好高度吻合`,
        confidence: 0.88,
      });
    }
  }

  // 基于距离的原因
  if (item.distance) {
    const distNum = parseFloat(item.distance);
    if (distNum < 2) {
      reasons.push({
        type: '便利性',
        text: `距离仅 ${item.distance}，步行即可到达`,
        confidence: 0.90,
      });
    }
  }

  // 兜底原因
  if (reasons.length === 0) {
    reasons.push({ type: '综合', text: '基于多维数据综合评估后推荐', confidence: 0.75 });
  }

  const overallConfidence = reasons.reduce((s, r) => s + r.confidence, 0) / reasons.length;

  return { reasons, overallConfidence: Math.round(overallConfidence * 100) / 100 };
};

/**
 * 策略检查 — 检查操作是否符合策略
 * @param {string} action - 操作类型: 'search' | 'recommend' | 'book' | 'order' | 'payment' | 'cancel'
 * @param {object} item - 操作对象
 * @returns {Promise<object>} { allowed, requireConfirm, reason, riskLevel }
 */
export const checkPolicy = async (action, item) => {
  await delay(200 + Math.random() * 150);

  const policyRules = {
    search: { allowed: true, requireConfirm: false, riskLevel: 'none' },
    recommend: { allowed: true, requireConfirm: false, riskLevel: 'none' },
    book: { allowed: true, requireConfirm: true, riskLevel: 'low', reason: '预约操作需用户确认时间' },
    order: { allowed: true, requireConfirm: true, riskLevel: 'medium', reason: '下单操作需用户确认商品与金额' },
    payment: { allowed: true, requireConfirm: true, riskLevel: 'high', reason: '支付操作涉及资金安全，需二次确认' },
    cancel: { allowed: true, requireConfirm: true, riskLevel: 'medium', reason: '取消操作不可逆，需用户确认' },
  };

  const rule = policyRules[action] || { allowed: true, requireConfirm: false, riskLevel: 'none' };

  // 高额额外校验
  if (item?.amount > 5000 || item?.price > 5000) {
    return {
      allowed: true,
      requireConfirm: true,
      reason: '金额超过 ¥5000，触发高额二次确认策略',
      riskLevel: 'high',
    };
  }

  return {
    allowed: rule.allowed,
    requireConfirm: rule.requireConfirm,
    reason: rule.reason || '操作符合策略，自动执行',
    riskLevel: rule.riskLevel,
  };
};

/**
 * 动态重规划 — 基于异常事件重新规划
 * @param {object} originalPlan - 原始行程方案
 * @param {object} event - 异常事件 { type: 'weather' | 'traffic' | 'closure' | 'delay', description: string }
 * @returns {Promise<object>} { newPlan, changes: [{ type, description }], reason }
 */
export const replanTrip = async (originalPlan, event) => {
  await delay(600 + Math.random() * 400);
  const changes = [];
  const newTimeline = [...(originalPlan.timeline || [])];

  switch (event?.type) {
    case 'weather':
      changes.push({ type: '路线调整', description: `因${event.description}，将户外活动调整为室内方案` });
      changes.push({ type: '时间调整', description: '出发时间提前 30 分钟以避开暴雨时段' });
      if (newTimeline.length > 0) {
        newTimeline[0] = { ...newTimeline[0], detail: newTimeline[0].detail + ' (雨天备选: 地铁出行)' };
      }
      break;
    case 'traffic':
      changes.push({ type: '交通调整', description: `因${event.description}，切换为地铁 + 步行方案` });
      changes.push({ type: '时间调整', description: '各行程节点延后 20 分钟' });
      break;
    case 'closure':
      changes.push({ type: '商家替换', description: `目标商家因${event.description}，已替换为同类型高评分备选` });
      break;
    case 'delay':
      changes.push({ type: '行程压缩', description: `因${event.description}，合并部分节点以保证核心体验` });
      if (newTimeline.length > 3) {
        newTimeline.splice(newTimeline.length - 2, 1);
        changes.push({ type: '节点删减', description: '移除次要节点，保留核心行程' });
      }
      break;
    default:
      changes.push({ type: '通用调整', description: `因${event?.description || '未知事件'}，对方案进行优化调整` });
  }

  return {
    newPlan: { ...originalPlan, timeline: newTimeline, planId: `plan_${Date.now()}` },
    changes,
    reason: `检测到「${event?.description || '计划变更'}」异常事件，已自动触发重规划逻辑`,
  };
};

/**
 * 商品搜索 — 搜索商品并返回结果
 * @param {string} query - 搜索关键词
 * @param {object} filters - 筛选条件 { minPrice?, maxPrice?, platform?, minRating? }
 * @returns {Promise<object>} { products: [{ id, name, price, platform, rating, matchScore, reviews }] }
 */
export const searchProducts = async (query, filters = {}) => {
  await delay(500 + Math.random() * 300);

  const allProducts = [
    { id: 'pd_001', name: 'Keychron K8 Pro 红轴机械键盘', price: 568, platform: '京东', rating: 4.7, matchScore: 0.94, reviews: 2341 },
    { id: 'pd_002', name: 'HHKB Professional 静电容键盘', price: 1980, platform: '天猫', rating: 4.9, matchScore: 0.87, reviews: 856 },
    { id: 'pd_003', name: 'Leopold FC660M 红轴', price: 899, platform: '淘宝', rating: 4.6, matchScore: 0.91, reviews: 1567 },
    { id: 'pd_004', name: '罗技 MX Keys Mini 无线键盘', price: 699, platform: '京东', rating: 4.8, matchScore: 0.82, reviews: 4521 },
    { id: 'pd_005', name: 'IKCO 610TS Cherry 红轴', price: 459, platform: '拼多多', rating: 4.4, matchScore: 0.78, reviews: 892 },
    { id: 'pd_006', name: 'Niz 84 静电容键盘', price: 758, platform: '天猫', rating: 4.5, matchScore: 0.85, reviews: 423 },
  ];

  let products = allProducts.filter((p) => p.matchScore >= 0.8);

  // 价格筛选
  if (filters.minPrice != null) products = products.filter((p) => p.price >= filters.minPrice);
  if (filters.maxPrice != null) products = products.filter((p) => p.price <= filters.maxPrice);
  // 平台筛选
  if (filters.platform) products = products.filter((p) => p.platform === filters.platform);
  // 评分筛选
  if (filters.minRating) products = products.filter((p) => p.rating >= filters.minRating);

  products.sort((a, b) => b.matchScore - a.matchScore);

  return { products };
};

/**
 * 本地服务匹配 — 匹配本地服务商
 * @param {string} serviceType - 服务类型: 'cleaning' | 'repair' | 'beauty' | 'fitness' | 'education'
 * @param {object} location - 位置信息 { city, district, lat?, lng? }
 * @returns {Promise<object>} { services: [{ id, name, rating, distance, price, availableSlots }] }
 */
export const matchLocalServices = async (serviceType, location) => {
  await delay(400 + Math.random() * 300);

  const servicePool = {
    cleaning: [
      { id: 'ls_001', name: '天鹅到家 深度保洁', rating: 4.8, distance: '0.8km', price: '¥120/次', availableSlots: ['明日 09:00', '明日 14:00', '后天 10:00'] },
      { id: 'ls_002', name: '河狸家 家政清洁', rating: 4.6, distance: '1.2km', price: '¥99/次', availableSlots: ['今日 16:00', '明日 09:00', '明日 15:00'] },
      { id: 'ls_003', name: '好慷在家 全屋保洁', rating: 4.9, distance: '2.5km', price: '¥158/次', availableSlots: ['明日 08:00', '后天 09:00'] },
    ],
    repair: [
      { id: 'ls_004', name: '极修匠 家电维修', rating: 4.7, distance: '1.5km', price: '¥80上门费', availableSlots: ['今日 14:00', '明日 10:00', '明日 16:00'] },
      { id: 'ls_005', name: '京东服务+ 空调清洗', rating: 4.5, distance: '3.0km', price: '¥129/次', availableSlots: ['明日 09:00', '明日 13:00', '后天 11:00'] },
    ],
    beauty: [
      { id: 'ls_006', name: '河狸家 美甲美睫', rating: 4.8, distance: '0.5km', price: '¥168起', availableSlots: ['今日 15:00', '今日 17:00', '明日 10:00'] },
      { id: 'ls_007', name: '悦发型 烫染护理', rating: 4.6, distance: '1.8km', price: '¥299起', availableSlots: ['明日 11:00', '明日 14:00'] },
    ],
    fitness: [
      { id: 'ls_008', name: '乐刻运动 月卡', rating: 4.4, distance: '0.3km', price: '¥199/月', availableSlots: ['随时开通', '门店 06:00-22:00'] },
      { id: 'ls_009', name: '超级猩猩 团课', rating: 4.7, distance: '1.0km', price: '¥69/节', availableSlots: ['今日 19:00', '明早 07:00', '明日 12:00'] },
    ],
    education: [
      { id: 'ls_010', name: '猿编程 少儿编程', rating: 4.9, distance: '2.0km', price: '¥199/节', availableSlots: ['明日 15:00', '周六 10:00'] },
      { id: 'ls_011', name: '新东方 雅思托福', rating: 4.5, distance: '1.5km', price: '¥299/节', availableSlots: ['明日 18:00', '周日 09:00'] },
    ],
  };

  const services = servicePool[serviceType] || [
    { id: 'ls_default', name: '综合服务商', rating: 4.5, distance: '1.0km', price: '¥面议', availableSlots: ['预约咨询'] },
  ];

  return { services };
};

/**
 * 商家分析 — 分析商家经营数据
 * @param {string} businessId - 商家 ID
 * @returns {Promise<object>} { metrics, trends, reviews, competitors, suggestions }
 */
export const analyzeBusiness = async (businessId) => {
  await delay(500 + Math.random() * 400);

  return {
    metrics: {
      monthlyRevenue: '¥18.5万',
      monthlyOrders: 1240,
      avgOrderValue: 149,
      repeatRate: 38,
      newCustomerRatio: 45,
    },
    trends: {
      revenue: [
        { month: '2026-05', value: 16.2 },
        { month: '2026-06', value: 17.1 },
        { month: '2026-07', value: 18.5 },
      ],
      orders: [
        { month: '2026-05', value: 1080 },
        { month: '2026-06', value: 1150 },
        { month: '2026-07', value: 1240 },
      ],
    },
    reviews: {
      avgRating: 4.7,
      totalReviews: 2847,
      positiveRate: 92,
      topKeywords: ['环境精致', '服务周到', '食材新鲜', '性价比高'],
      recentNegative: ['等位时间较长', '停车位紧张'],
    },
    competitors: [
      { name: '竞品 A', distance: '0.5km', rating: 4.5, advantage: '价格更低' },
      { name: '竞品 B', distance: '1.2km', rating: 4.6, advantage: '品牌知名度' },
    ],
    suggestions: [
      '优化高峰期等位流程，减少顾客流失',
      '增加线上预约比例，降低空置率',
      '推出会员复购激励，提升复购率至 45%',
      '补充停车位指引或代客泊车服务',
    ],
  };
};

// ═══════════════════════════════════════════════════
// Memory Agent — 记忆管理系统
// ═══════════════════════════════════════════════════

const MEMORY_STORAGE_KEY = 'simon_memory_data';
const PREFERENCES_STORAGE_KEY = 'simon_user_preferences';
const BEHAVIOR_STORAGE_KEY = 'simon_behavior_history';
const LEARNED_STORAGE_KEY = 'simon_learned_preferences';

/**
 * 从 localStorage 获取存储对象
 */
function getStorage(key) {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

/**
 * 保存对象到 localStorage
 */
function setStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/**
 * 返回每类记忆的默认 Mock 数据
 */
function getDefaultMemories() {
  return {
    short: [
      { id: 'm1', content: '当前会话: 周末约会场景规划中', weight: 1.0, createdAt: Date.now() },
      { id: 'm2', content: '预算约束: ¥500 以内', weight: 0.9, createdAt: Date.now() },
      { id: 'm3', content: '地理范围: 市中心 5km', weight: 0.85, createdAt: Date.now() },
      { id: 'm4', content: '时间窗口: 今晚 18:00-22:00', weight: 0.8, createdAt: Date.now() },
    ],
    long: [
      { id: 'm5', content: '月消费均值: ¥8,500', weight: 0.7, createdAt: Date.now() - 86400000 * 30 },
      { id: 'm6', content: '累计完成订单: 12 次', weight: 0.65, createdAt: Date.now() - 86400000 * 60 },
      { id: 'm7', content: '常见城市: 上海、杭州、北京', weight: 0.6, createdAt: Date.now() - 86400000 * 90 },
    ],
    preference: [
      { id: 'm8', content: '咖啡偏好: 手冲 > 美式 > 拿铁', weight: 0.95, createdAt: Date.now() - 86400000 * 7 },
      { id: 'm9', content: '餐厅氛围: 安静私密 > 热闹', weight: 0.9, createdAt: Date.now() - 86400000 * 14 },
      { id: 'm10', content: '排队容忍度: 最长 15 分钟', weight: 0.88, createdAt: Date.now() - 86400000 * 21 },
    ],
    episodic: [
      { id: 'm11', content: '2026-07: 亲子迪士尼一日游', weight: 0.7, createdAt: Date.now() - 86400000 * 45 },
      { id: 'm12', content: '2026-06: 云南一周旅行', weight: 0.65, createdAt: Date.now() - 86400000 * 90 },
      { id: 'm13', content: '2026-05: 北京商务出差', weight: 0.6, createdAt: Date.now() - 86400000 * 120 },
      { id: 'm14', content: '2026-04: 西湖春游野餐', weight: 0.55, createdAt: Date.now() - 86400000 * 150 },
    ],
  };
}

function getDefaultPreferences() {
  return [
    { id: 'p1', label: '安静酒店', category: 'travel', strength: 92, source: 'explicit', createdAt: Date.now() - 86400000 * 30 },
    { id: 'p2', label: '不喜早起', category: 'lifestyle', strength: 88, source: 'learned', createdAt: Date.now() - 86400000 * 15 },
    { id: 'p3', label: '预算中等', category: 'budget', strength: 75, source: 'explicit', createdAt: Date.now() - 86400000 * 45 },
    { id: 'p4', label: '喜欢咖啡', category: 'dining', strength: 95, source: 'learned', createdAt: Date.now() - 86400000 * 7 },
    { id: 'p5', label: '喜欢拍照', category: 'lifestyle', strength: 82, source: 'learned', createdAt: Date.now() - 86400000 * 20 },
    { id: 'p6', label: '不喜欢排队', category: 'social', strength: 90, source: 'explicit', createdAt: Date.now() - 86400000 * 60 },
  ];
}

function getDefaultBehaviors() {
  return [
    { id: 'b1', type: 'search', title: '搜索: 上海日料餐厅推荐', description: '筛选了 28 家日料餐厅，关注评分和氛围', timestamp: Date.now() - 3600000 * 2 },
    { id: 'b2', type: 'browse', title: '浏览: 月見·庭院日料', description: '查看详情 4 分钟，关注了庭院景观和套餐', timestamp: Date.now() - 3600000 * 5 },
    { id: 'b3', type: 'favorite', title: '收藏: 湖畔咖啡', description: '标记为常去咖啡店，喜欢手冲品类', timestamp: Date.now() - 3600000 * 8 },
    { id: 'b4', type: 'search', title: '搜索: 周末亲子活动', description: '查找适合带娃的户外活动场所', timestamp: Date.now() - 86400000 * 1 },
    { id: 'b5', type: 'booking', title: '预约: 周六晚餐位', description: '预约月見·庭院日料 18:30 景观位', timestamp: Date.now() - 86400000 * 2 },
    { id: 'b6', type: 'share', title: '分享: 云南旅行攻略', description: '将自定义旅行路线分享给朋友', timestamp: Date.now() - 86400000 * 3 },
  ];
}

function getDefaultLearned() {
  return [
    { id: 'l1', label: '手冲咖啡 > 美式', confidence: 0.94, frequency: 12, lastSeen: Date.now() - 86400000, relatedTags: ['喜欢咖啡', '品质优先'] },
    { id: 'l2', label: '晚餐时间偏好 18:30-19:30', confidence: 0.88, frequency: 8, lastSeen: Date.now() - 86400000 * 2, relatedTags: ['不喜早起', '规律作息'] },
    { id: 'l3', label: '餐厅选择: 评分 > 4.5', confidence: 0.91, frequency: 15, lastSeen: Date.now() - 3600000 * 5, relatedTags: ['品质优先', '日料偏好'] },
    { id: 'l4', label: '步行距离: < 2km', confidence: 0.82, frequency: 6, lastSeen: Date.now() - 86400000, relatedTags: ['市中心', '便利优先'] },
    { id: 'l5', label: '周末偏好户外活动', confidence: 0.76, frequency: 5, lastSeen: Date.now() - 86400000 * 7, relatedTags: ['周末出行', '喜欢拍照'] },
    { id: 'l6', label: '预算区间: ¥200-800/餐', confidence: 0.85, frequency: 10, lastSeen: Date.now() - 86400000 * 3, relatedTags: ['预算中等', '品质优先'] },
    { id: 'l7', label: '环境偏好: 安静/私密', confidence: 0.93, frequency: 9, lastSeen: Date.now() - 86400000 * 2, relatedTags: ['安静酒店', '不喜欢排队'] },
  ];
}

// ═══════════════════════════════════════════════════
// Memory Agent 公开 API
// ═══════════════════════════════════════════════════

/**
 * 获取分层记忆数据
 * 如果 localStorage 无数据则初始化默认数据
 * @returns {Promise<object>} 包含 short/long/preference/episodic 四层的记忆数据
 */
export async function getMemories() {
  await delay(300);
  const stored = getStorage(MEMORY_STORAGE_KEY);
  if (stored) return stored;

  const defaults = getDefaultMemories();
  setStorage(MEMORY_STORAGE_KEY, defaults);
  return defaults;
}

/**
 * 保存一条新记忆
 * @param {object} memory - { category: 'short'|'long'|'preference'|'episodic', content: string, weight?: number }
 * @returns {Promise<object>} 保存后的完整记忆条目
 */
export async function saveMemory(memory) {
  await delay(200);
  const memories = await getMemories();

  const newItem = {
    id: `m_${Date.now()}`,
    content: memory.content,
    weight: memory.weight ?? 0.8,
    createdAt: Date.now(),
    ...memory,
  };

  const category = memory.category || 'short';
  if (!memories[category]) memories[category] = [];
  memories[category].unshift(newItem);

  setStorage(MEMORY_STORAGE_KEY, memories);
  return newItem;
}

/**
 * 删除一条记忆
 * @param {string} memoryId - 记忆 ID
 * @param {string} category - 记忆分类
 * @returns {Promise<boolean>}
 */
export async function deleteMemory(memoryId, category) {
  await delay(150);
  const memories = await getMemories();
  if (memories[category]) {
    memories[category] = memories[category].filter((item) => item.id !== memoryId);
    setStorage(MEMORY_STORAGE_KEY, memories);
    return true;
  }
  return false;
}

/**
 * 获取用户偏好标签
 * @returns {Promise<Array>} 偏好标签数组
 */
export async function getUserPreferences() {
  await delay(200);
  const stored = getStorage(PREFERENCES_STORAGE_KEY);
  if (stored) return stored;

  const defaults = getDefaultPreferences();
  setStorage(PREFERENCES_STORAGE_KEY, defaults);
  return defaults;
}

/**
 * 添加用户偏好
 * @param {object} preference - { label: string, category: string, strength?: number }
 * @returns {Promise<object>} 新偏好条目
 */
export async function addUserPreference(preference) {
  await delay(200);
  const preferences = await getUserPreferences();

  const newPref = {
    id: `p_${Date.now()}`,
    label: preference.label,
    category: preference.category || 'lifestyle',
    strength: preference.strength ?? 70,
    source: 'explicit',
    createdAt: Date.now(),
  };

  preferences.unshift(newPref);
  setStorage(PREFERENCES_STORAGE_KEY, preferences);
  return newPref;
}

/**
 * 移除用户偏好
 * @param {string} prefId - 偏好 ID
 * @returns {Promise<boolean>}
 */
export async function removeUserPreference(prefId) {
  await delay(150);
  const preferences = await getUserPreferences();
  const filtered = preferences.filter((p) => p.id !== prefId);
  setStorage(PREFERENCES_STORAGE_KEY, filtered);
  return filtered.length < preferences.length;
}

/**
 * 获取行为历史记录
 * @param {object} options - { type?: string, limit?: number }
 * @returns {Promise<Array>} 行为记录数组
 */
export async function getMemoryHistory(options = {}) {
  await delay(250);
  const stored = getStorage(BEHAVIOR_STORAGE_KEY);
  let behaviors = stored || getDefaultBehaviors();

  if (!stored) setStorage(BEHAVIOR_STORAGE_KEY, behaviors);

  if (options.type && options.type !== 'all') {
    behaviors = behaviors.filter((b) => b.type === options.type);
  }

  if (options.limit) {
    behaviors = behaviors.slice(0, options.limit);
  }

  return behaviors.sort((a, b) => b.timestamp - a.timestamp);
}

/**
 * 添加行为记录
 * @param {object} record - { type: string, title: string, description: string, metadata?: object }
 * @returns {Promise<object>} 新行为记录
 */
export async function addBehaviorRecord(record) {
  await delay(150);
  const behaviors = await getMemoryHistory();

  const newRecord = {
    id: `b_${Date.now()}`,
    type: record.type || 'browse',
    title: record.title,
    description: record.description || '',
    timestamp: Date.now(),
    metadata: record.metadata || {},
  };

  behaviors.unshift(newRecord);
  setStorage(BEHAVIOR_STORAGE_KEY, behaviors);
  return newRecord;
}

/**
 * 获取 AI 学习到的偏好
 * @returns {Promise<Array>} AI 学习偏好数组
 */
export async function getLearnedPreferences() {
  await delay(200);
  const stored = getStorage(LEARNED_STORAGE_KEY);
  if (stored) return stored;

  const defaults = getDefaultLearned();
  setStorage(LEARNED_STORAGE_KEY, defaults);
  return defaults;
}

/**
 * 清空所有记忆数据（用于重置）
 * @returns {Promise<boolean>}
 */
export async function clearAllMemories() {
  await delay(100);
  localStorage.removeItem(MEMORY_STORAGE_KEY);
  localStorage.removeItem(PREFERENCES_STORAGE_KEY);
  localStorage.removeItem(BEHAVIOR_STORAGE_KEY);
  localStorage.removeItem(LEARNED_STORAGE_KEY);
  return true;
}

/**
 * 获取记忆统计概览
 * @returns {Promise<object>} 统计信息
 */
export async function getMemoryStats() {
  const [memories, preferences, behaviors, learned] = await Promise.all([
    getMemories(),
    getUserPreferences(),
    getMemoryHistory(),
    getLearnedPreferences(),
  ]);

  return {
    totalMemories: Object.values(memories).reduce((sum, arr) => sum + arr.length, 0),
    categoryCounts: {
      short: memories.short?.length || 0,
      long: memories.long?.length || 0,
      preference: memories.preference?.length || 0,
      episodic: memories.episodic?.length || 0,
    },
    preferenceCount: preferences.length,
    explicitCount: preferences.filter((p) => p.source === 'explicit').length,
    learnedCount: preferences.filter((p) => p.source === 'learned').length,
    behaviorCount: behaviors.length,
    aiInsightsCount: learned.length,
  };
}

export default {
  AGENT_REGISTRY,
  SCENARIO_TEMPLATES,
  executeAgentPipeline,
  simulateTrip,
  analyzeRisk,
  explainRecommendation,
  checkPolicy,
  replanTrip,
  searchProducts,
  matchLocalServices,
  analyzeBusiness,
  getMemories,
  saveMemory,
  deleteMemory,
  getUserPreferences,
  addUserPreference,
  removeUserPreference,
  getMemoryHistory,
  addBehaviorRecord,
  getLearnedPreferences,
  clearAllMemories,
  getMemoryStats,
};
